import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import {useMutation} from '@apollo/react-hooks'
import {Form, Input, notification} from 'antd'
import Heading from 'components/shared/Heading'
import Button from 'components/shared/Button'
import {ADDITIONAL_ESTATE_SELLER_TYPE} from 'lib/estateSellerConsts'
import {estateInfoQuery} from 'components/PrivateRoutes/Estate/EstateInfo'

const removeEstateSellerMutation = gql`
  mutation RemoveEstateSeller($input: RemoveEstateSellerInput!) {
    RemoveEstateSeller(input: $input) {
      estate {
        estateSellers {
          id, type, name, phone, email
        }
      }
    }
  }
`

const addOrUpdateEstateSellerMutation = gql`
  mutation AddOrUpdateEstateSeller($input: AddOrUpdateEstateSellerInput!) {
    AddOrUpdateEstateSeller(input: $input) {
      person {
        id
      }
    }
  }
`

const ContactPersonFormFields = ({onSubmit, isSubmitting, person}) => {
  const [form] = Form.useForm()

  useEffect(() => {
    form.resetFields()
  }, [form, person])

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={person}
      onFinish={onSubmit}
    >
      <Form.Item
        name="name"
        label="Navn"
        rules={[
          {required: true, message: 'Skriv inn fullt navn'},
          {pattern: /\s+/, message: 'Skriv inn fullt navn'},
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="email"
        label="E-post"
        rules={[
          {required: true, message: 'Skriv inn gyldig e-post'},
          {type: 'email', message: 'Skriv inn gyldig e-post'},
        ]}
      >
          <Input />
      </Form.Item>
      <Form.Item
        name="phone"
        label="Telefon"
        rules={[
          {required: true, message: 'Skriv inn gyldig telefonnummer'},
          {pattern: /^\+?\d{8,}$/, message: 'Skriv inn gyldig telefonnummer'},
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item shouldUpdate>
        {() => (
          <Button
            type="action"
            htmlType="submit"
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              !form.isFieldsTouched() ||
              Object.values(form.getFieldsValue()).some(v => !v) || // all fields are mandatory
              form.getFieldsError().filter(({errors}) => errors.length).length
            }
          >
            {person.id ? 'Lagre endringer' : 'Legg til'}
          </Button>
        )}
      </Form.Item>
    </Form>
  )
}

const ContactPersonForm = ContactPersonFormFields

const ContactPerson = ({person, estateId, onSubmittedSeller, onRemoveSeller}) => {
  const refetchQueries = [
    {
      query: estateInfoQuery,
      variables: {
        estateId,
      }
    }
  ]
  const [removeEstateSeller, {loading: isDeleting}] = useMutation(removeEstateSellerMutation, {
    refetchQueries,
    onCompleted: () => {
      notification['success']({
        message: 'Kontaktperson slettet',
        description: 'Kontaktpersonen ble slettet.',
      })
    },
    onError: () => {
      notification['error']({
        message: 'Sletting feilet',
        description: 'Noe gikk galt med fjerning av kontaktperson. Prøv igjen senere.',
      })
    }
  })
  const [addOrUpdateEstateSeller, {loading: isSubmitting}] = useMutation(addOrUpdateEstateSellerMutation, {
    refetchQueries,
    onCompleted: () => {
      notification['success']({
        message: 'Kontaktperson lagret',
        description: 'Kontaktpersonen ble lagret.',
      })
    },
    onError: () => {
      notification['error']({
        message: 'Lagring feilet',
        description: 'Noe gikk galt ved lagring av kontaktperson. Prøv igjen senere.',
      })
    }
  })

  return (
    <div className="contact-person">
      {person.type === ADDITIONAL_ESTATE_SELLER_TYPE && (
        <Heading level={4}>
          Alternativ kontaktperson
          <Button
            secondary
            type="action"
            className="remove-button"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              if (onRemoveSeller) {
                return onRemoveSeller(person)
              }
              removeEstateSeller({
                variables: {
                  input: {
                    estateId,
                    personId: person.id,
                  }
                }
              })
            }}
          >
            Fjern
          </Button>
        </Heading>
      )}

      <ContactPersonForm
        person={person}
        onSubmit={(values) => {
          addOrUpdateEstateSeller({
            variables: {
              input: {
                estateId,
                id: person.id,
                type: person.type,
                name: values.name,
                email: values.email,
                phone: values.phone,
              }
            }
          }).then(({data}) => {
            if (onSubmittedSeller) {
              return onSubmittedSeller(data.AddOrUpdateEstateSeller.person)
            }
          })
        }}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

ContactPerson.propTypes = {
  person: PropTypes.object.isRequired,
  estateId: PropTypes.string.isRequired,
}

export default ContactPerson
